<!--
  APP 端云控面板（P0）。

  三个子模块：版本与更新 / 功能开关 / 安全策略，全部按「平台 + 环境」隔离：
  顶部切换平台（当前只有安卓端，iOS / 鸿蒙为预留占位）与环境（生产 / 预发 / 测试），
  切换后下面所有内容重新加载。

  ⚠️ 这里只是**配置台**，不直接下发。客户端拉的是 /api/v1/app-config/bundle，
     下发的判定（灰度命中、白名单、时间窗、最低可用版本）全在后端一处实现，
     本页面不做任何本地推算 —— 否则「后台看到已放量、客户端拿不到」会非常难查。

  ⚠️ 版本发布是状态机：草稿 → 预发布（仅白名单）→ 已发布 → 已下线。
     新建一律是草稿，「发布」必须再点一次，这是刻意加的摩擦。
-->
<template>
  <div>
    <!-- ------------------------------ 下发范围 ------------------------------ -->
    <div class="adm-card">
      <div class="adm-card-head">
        <div class="adm-card-title">
          <span class="adm-card-title-bar" />下发范围
          <span class="adm-card-title-extra">平台与环境互相隔离，切换后下方内容会重新加载</span>
        </div>
        <div class="adm-head-actions">
          <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="reloadAll">
            刷新
          </el-button>
        </div>
      </div>

      <div class="adm-filter">
        <el-radio-group v-model="platform" size="small" @change="reloadAll">
          <el-radio-button label="ANDROID">安卓端</el-radio-button>
          <el-radio-button label="IOS" disabled>iOS 端（预留）</el-radio-button>
          <el-radio-button label="HARMONY" disabled>鸿蒙端（预留）</el-radio-button>
        </el-radio-group>
        <el-select v-model="env" class="adm-w140" size="small" @change="reloadAll">
          <el-option label="生产" value="PROD" />
          <el-option label="预发" value="PRE" />
          <el-option label="测试" value="TEST" />
        </el-select>
      </div>

      <div v-if="env !== 'PROD'" class="adm-alert is-warning">
        <i class="el-icon-warning-outline" />
        <span>
          当前是「{{ envLabel }}」环境：这里的配置只会下发给 <code>env={{ env }}</code>
          的客户端，不影响生产。反之生产配置也绝不会出现在这个环境。
        </span>
      </div>
    </div>

    <!-- ------------------------------ 子模块 ------------------------------ -->
    <el-tabs v-model="tab" class="adm-app-tabs">
      <!-- ============================ 版本与更新 ============================ -->
      <el-tab-pane label="版本与更新" name="versions">
        <div class="adm-card">
          <div class="adm-card-head">
            <div class="adm-card-title">
              <span class="adm-card-title-bar" />版本列表
              <span class="adm-card-title-extra">共 {{ versions.length }} 个</span>
            </div>
            <div class="adm-head-actions">
              <span class="adm-inline-hint">版本检测</span>
              <el-switch
                v-model="policy.updateCheckEnabled"
                :disabled="policySaving"
                @change="onToggleUpdateCheck"
              />
              <el-button size="small" type="primary" icon="el-icon-plus" @click="openVersionDialog()">
                新建版本
              </el-button>
            </div>
          </div>

          <div v-if="!policy.updateCheckEnabled" class="adm-alert is-warning">
            <i class="el-icon-warning-outline" />
            <span>
              版本检测总开关已关闭：客户端不会弹任何更新提示（接口仍返回该字段）。
              这是应急止血用的开关，正常情况下请保持开启。
            </span>
          </div>

          <div v-if="!loading && !versions.length" class="adm-empty">
            <i class="el-icon-mobile-phone" />还没有配置任何版本
          </div>

          <el-table v-else v-loading="loading" :data="versions" border>
            <el-table-column label="版本" min-width="150">
              <template slot-scope="{ row }">
                <div class="adm-app-ver">
                  <span class="adm-app-ver-name">{{ row.versionName }}</span>
                  <span class="adm-code">code {{ row.versionCode }}</span>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="状态" width="100">
              <template slot-scope="{ row }">
                <el-tag :type="statusTagType(row.status)" size="mini">{{ row.statusLabel }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="更新类型" width="100">
              <template slot-scope="{ row }">
                <el-tag :type="updateTypeTagType(row.updateType)" size="mini">
                  {{ row.updateTypeLabel }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="灰度" width="130">
              <template slot-scope="{ row }">
                <span>{{ row.grayPercent }}%</span>
                <span v-if="row.whitelist && row.whitelist.length" class="adm-muted">
                  · 白名单 {{ row.whitelist.length }}
                </span>
              </template>
            </el-table-column>
            <el-table-column label="最低可用版本" width="120">
              <template slot-scope="{ row }">
                <span v-if="row.minVersionCode > 0">code {{ row.minVersionCode }}</span>
                <span v-else class="adm-muted">不设下限</span>
              </template>
            </el-table-column>
            <el-table-column label="生效时间" width="170">
              <template slot-scope="{ row }">
                <span v-if="!row.startTime && !row.endTime" class="adm-muted">长期</span>
                <span v-else>{{ timeRange(row) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="下载入口" width="110">
              <template slot-scope="{ row }">
                <span v-if="row.downloadUrl">直链</span>
                <span v-if="row.storeUrls && row.storeUrls.length">
                  {{ row.downloadUrl ? ' + ' : '' }}商店 {{ row.storeUrls.length }}
                </span>
                <span v-if="!row.downloadUrl && (!row.storeUrls || !row.storeUrls.length)" class="adm-muted">
                  未配置
                </span>
              </template>
            </el-table-column>
            <el-table-column label="更新时间" width="150">
              <template slot-scope="{ row }">{{ formatTime(row.updatedAt) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="220" fixed="right">
              <template slot-scope="{ row }">
                <div class="adm-op-actions">
                  <el-link type="primary" :underline="false" @click="openVersionDialog(row)">
                    编辑
                  </el-link>
                  <el-link
                    v-if="canTransit(row, 'PRE')"
                    type="warning"
                    :underline="false"
                    @click="onChangeVersionStatus(row, 'PRE')"
                  >预发布</el-link>
                  <el-link
                    v-if="canTransit(row, 'PUBLISHED')"
                    type="success"
                    :underline="false"
                    @click="onChangeVersionStatus(row, 'PUBLISHED')"
                  >{{ row.status === 'OFFLINE' ? '重新发布' : '发布' }}</el-link>
                  <el-link
                    v-if="canTransit(row, 'OFFLINE')"
                    type="info"
                    :underline="false"
                    @click="onChangeVersionStatus(row, 'OFFLINE')"
                  >下线</el-link>
                  <el-link
                    v-if="row.status === 'DRAFT' || row.status === 'OFFLINE'"
                    type="danger"
                    :underline="false"
                    @click="onDeleteVersion(row)"
                  >删除</el-link>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>

      <!-- ============================ 功能开关 ============================ -->
      <el-tab-pane label="功能开关" name="flags">
        <div class="adm-card">
          <div class="adm-card-head">
            <div class="adm-card-title">
              <span class="adm-card-title-bar" />开关列表
              <span class="adm-card-title-extra">共 {{ flags.length }} 个 · 不发版即可上下功能</span>
            </div>
            <div class="adm-head-actions">
              <el-button size="small" type="primary" icon="el-icon-plus" @click="openFlagDialog()">
                新建开关
              </el-button>
            </div>
          </div>

          <div class="adm-alert">
            <i class="el-icon-info" />
            <span>
              「总闸」关掉即对所有客户端为 false（白名单也不破例）；「兜底值」是客户端
              <b>拉取失败</b>时用的值 —— 新功能灰度期应该设成关闭，避免接口抖动把灰度功能误开给所有人。
            </span>
          </div>

          <div v-if="!loading && !flags.length" class="adm-empty">
            <i class="el-icon-s-operation" />还没有配置任何开关
          </div>

          <el-table v-else v-loading="loading" :data="flags" border>
            <el-table-column label="开关" min-width="200">
              <template slot-scope="{ row }">
                <div class="adm-code">{{ row.flagKey }}</div>
                <div v-if="row.description" class="adm-sub">{{ row.description }}</div>
              </template>
            </el-table-column>
            <el-table-column prop="name" label="名称" width="150" show-overflow-tooltip />
            <el-table-column label="分组" width="110">
              <template slot-scope="{ row }">
                <el-tag size="mini" type="info">{{ row.group }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="总闸" width="90">
              <template slot-scope="{ row }">
                <el-switch v-model="row.enabled" @change="onToggleFlag(row)" />
              </template>
            </el-table-column>
            <el-table-column label="生效范围" width="140">
              <template slot-scope="{ row }">
                <span v-if="row.rolloutType === 'ALL'">全量</span>
                <span v-else-if="row.rolloutType === 'PERCENT'">{{ row.rolloutPercent }}% 灰度</span>
                <span v-else>白名单 {{ (row.whitelist || []).length }} 个</span>
              </template>
            </el-table-column>
            <el-table-column label="兜底值" width="90">
              <template slot-scope="{ row }">
                <el-tag size="mini" :type="row.defaultValue ? 'success' : 'info'">
                  {{ row.defaultValue ? '开启' : '关闭' }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column label="生效时间" width="170">
              <template slot-scope="{ row }">
                <span v-if="!row.startTime && !row.endTime" class="adm-muted">长期</span>
                <span v-else>{{ timeRange(row) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="更新时间" width="150">
              <template slot-scope="{ row }">{{ formatTime(row.updatedAt) }}</template>
            </el-table-column>
            <el-table-column label="操作" width="120" fixed="right">
              <template slot-scope="{ row }">
                <div class="adm-op-actions">
                  <el-link type="primary" :underline="false" @click="openFlagDialog(row)">编辑</el-link>
                  <el-link type="danger" :underline="false" @click="onDeleteFlag(row)">删除</el-link>
                </div>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-tab-pane>

      <!-- ============================ 安全策略 ============================ -->
      <el-tab-pane label="安全策略" name="policy">
        <div class="adm-card">
          <div class="adm-card-head">
            <div class="adm-card-title">
              <span class="adm-card-title-bar" />平台策略
              <span class="adm-card-title-extra">
                {{ policy.persisted ? '最后修改 ' + formatTime(policy.updatedAt) : '尚未保存过，当前为代码默认值' }}
              </span>
            </div>
            <div class="adm-head-actions">
              <el-button
                size="small"
                type="primary"
                :loading="policySaving"
                @click="onSavePolicy"
              >保存策略</el-button>
            </div>
          </div>

          <el-form label-width="150px" class="adm-app-form">
            <el-form-item label="版本检测总开关">
              <el-switch v-model="policy.updateCheckEnabled" />
              <div class="adm-inline-hint">
                关闭后客户端不再弹更新提示。接口出问题 / 误配导致全员被拦时用它止血。
              </div>
            </el-form-item>

            <el-form-item label="强制下线">
              <el-switch v-model="policy.kickAllDevices" />
              <div class="adm-inline-hint">
                开启后所有 App 会话失效，下次启动必须重登。保存时由服务端记录生效时刻
                <span v-if="policy.kickAllAt">（当前：{{ formatTime(policy.kickAllAt) }}）</span>。
              </div>
            </el-form-item>

            <el-form-item label="热修复开关">
              <el-switch v-model="policy.hotfixEnabled" />
              <div class="adm-inline-hint">
                只有开启时客户端才允许加载热修补丁（补丁能力本身不在此模块内）。
              </div>
            </el-form-item>

            <el-form-item label="封禁版本">
              <el-input
                v-model="policyForm.blockedVersionsText"
                type="textarea"
                :rows="3"
                placeholder="填写版本号（整数），多个用换行或逗号分隔，例如：102, 103"
              />
              <div class="adm-inline-hint">
                命中即强制更新（前提是存在更高的可下发版本；没有目标版本时不会硬拦，
                否则用户无路可走）。与「最低可用版本」的区别：这里拉黑的是<b>具体版本</b>，
                那边拦的是<b>所有低于某个版本号</b>的客户端。
              </div>
            </el-form-item>

            <el-form-item label="调试白名单">
              <el-input
                v-model="policyForm.debugWhitelistText"
                type="textarea"
                :rows="3"
                placeholder="用户 ID 或设备 ID，多个用换行或逗号分隔"
              />
              <div class="adm-inline-hint">命中才允许客户端打开 debug 日志，便于远程排障。</div>
            </el-form-item>
          </el-form>
        </div>
      </el-tab-pane>
    </el-tabs>

    <!-- ------------------------------ 版本弹窗 ------------------------------ -->
    <el-dialog
      :title="versionForm.id ? '编辑版本' : '新建版本'"
      :visible.sync="versionDialogVisible"
      width="640px"
      append-to-body
      @closed="onVersionDialogClosed"
    >
      <el-form ref="versionForm" :model="versionForm" :rules="versionRules" label-width="110px">
        <el-form-item label="版本名" prop="versionName">
          <el-input v-model="versionForm.versionName" placeholder="展示用，例如 1.4.2" />
        </el-form-item>
        <el-form-item label="版本号" prop="versionCode">
          <el-input
            v-model.number="versionForm.versionCode"
            :disabled="!!versionForm.id"
            placeholder="整数，比较大小只认它"
          />
          <div v-if="versionForm.id" class="adm-inline-hint">
            版本号是唯一键的一部分，不可修改；要改请新建一个版本。
          </div>
        </el-form-item>
        <el-form-item label="最低可用版本">
          <el-input
            v-model.number="versionForm.minVersionCode"
            placeholder="低于该版本号的客户端强制更新；0 表示不设下限"
          />
        </el-form-item>
        <el-form-item label="更新类型" prop="updateType">
          <el-radio-group v-model="versionForm.updateType">
            <el-radio-button label="FORCE">强制更新</el-radio-button>
            <el-radio-button label="SUGGEST">建议更新</el-radio-button>
            <el-radio-button label="SILENT">静默更新</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="标题">
          <el-input v-model="versionForm.title" placeholder="更新弹窗标题，例如：发现新版本" />
        </el-form-item>
        <el-form-item label="更新日志">
          <el-input v-model="versionForm.notes" type="textarea" :rows="4" placeholder="每行一条" />
        </el-form-item>
        <el-form-item label="安装包直链">
          <el-input v-model="versionForm.downloadUrl" placeholder="https://...（必须是 http/https）" />
        </el-form-item>
        <el-form-item label="应用商店链接">
          <el-input
            v-model="versionForm.storeUrlsText"
            type="textarea"
            :rows="2"
            placeholder="每行一个 https 链接"
          />
        </el-form-item>
        <el-form-item label="灰度比例">
          <el-input-number v-model="versionForm.grayPercent" :min="0" :max="100" :step="5" />
          <span class="adm-inline-hint">按设备 ID 稳定哈希取模；100 = 全量</span>
        </el-form-item>
        <el-form-item label="灰度白名单">
          <el-input
            v-model="versionForm.whitelistText"
            type="textarea"
            :rows="2"
            placeholder="用户 ID 或设备 ID，多个用换行或逗号分隔；命中白名单一定可见"
          />
        </el-form-item>
        <el-form-item label="渠道范围">
          <el-input
            v-model="versionForm.channelScopeText"
            type="textarea"
            :rows="2"
            placeholder="留空 = 全渠道；填了则只对这些渠道下发"
          />
        </el-form-item>
        <el-form-item label="生效时间">
          <div class="adm-app-time">
            <el-date-picker
              v-model="versionForm.startTime"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              placeholder="开始（留空=立即）"
            />
            <span class="adm-muted">至</span>
            <el-date-picker
              v-model="versionForm.endTime"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              placeholder="结束（留空=长期）"
            />
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="versionDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="versionSaving" @click="onSaveVersion">
          保存
        </el-button>
      </div>
    </el-dialog>

    <!-- ------------------------------ 开关弹窗 ------------------------------ -->
    <el-dialog
      :title="flagForm.id ? '编辑开关' : '新建开关'"
      :visible.sync="flagDialogVisible"
      width="620px"
      append-to-body
      @closed="onFlagDialogClosed"
    >
      <el-form ref="flagForm" :model="flagForm" :rules="flagRules" label-width="110px">
        <el-form-item label="开关键" prop="flagKey">
          <el-input
            v-model="flagForm.flagKey"
            :disabled="!!flagForm.id"
            placeholder="客户端按它取值，例如 social_share_enabled"
          />
          <div v-if="flagForm.id" class="adm-inline-hint">
            开关键不可修改：改了等于换了一个开关，老客户端会永远读不到新键。
          </div>
        </el-form-item>
        <el-form-item label="名称" prop="name">
          <el-input v-model="flagForm.name" placeholder="显示名，例如：分享与好友" />
        </el-form-item>
        <el-form-item label="说明">
          <el-input v-model="flagForm.description" placeholder="关掉会发生什么（给未来的自己看）" />
        </el-form-item>
        <el-form-item label="分组">
          <el-select v-model="flagForm.group" class="adm-w180">
            <el-option label="功能（FEATURE）" value="FEATURE" />
            <el-option label="社交（SOCIAL）" value="SOCIAL" />
            <el-option label="内容（CONTENT）" value="CONTENT" />
            <el-option label="成就（ACHIEVEMENT）" value="ACHIEVEMENT" />
            <el-option label="活动（ACTIVITY）" value="ACTIVITY" />
            <el-option label="广告（AD）" value="AD" />
          </el-select>
        </el-form-item>
        <el-form-item label="总闸">
          <el-switch v-model="flagForm.enabled" />
          <span class="adm-inline-hint">关闭后对所有客户端为 false，白名单也不破例</span>
        </el-form-item>
        <el-form-item label="客户端兜底值">
          <el-switch v-model="flagForm.defaultValue" />
          <span class="adm-inline-hint">接口拉取失败时 App 用这个值</span>
        </el-form-item>
        <el-form-item label="生效范围">
          <el-radio-group v-model="flagForm.rolloutType">
            <el-radio-button label="ALL">全量</el-radio-button>
            <el-radio-button label="PERCENT">百分比灰度</el-radio-button>
            <el-radio-button label="WHITELIST">白名单</el-radio-button>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="flagForm.rolloutType === 'PERCENT'" label="灰度比例">
          <el-input-number v-model="flagForm.rolloutPercent" :min="0" :max="100" :step="5" />
        </el-form-item>
        <el-form-item label="白名单">
          <el-input
            v-model="flagForm.whitelistText"
            type="textarea"
            :rows="2"
            placeholder="用户 ID 或设备 ID，多个用换行或逗号分隔"
          />
        </el-form-item>
        <el-form-item label="生效时间">
          <div class="adm-app-time">
            <el-date-picker
              v-model="flagForm.startTime"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              placeholder="开始（留空=立即）"
            />
            <span class="adm-muted">至</span>
            <el-date-picker
              v-model="flagForm.endTime"
              type="datetime"
              value-format="yyyy-MM-dd HH:mm:ss"
              placeholder="结束（留空=长期）"
            />
          </div>
        </el-form-item>
      </el-form>
      <div slot="footer">
        <el-button size="small" @click="flagDialogVisible = false">取消</el-button>
        <el-button size="small" type="primary" :loading="flagSaving" @click="onSaveFlag">保存</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import {
  fetchAppVersions,
  createAppVersion,
  updateAppVersion,
  changeAppVersionStatus,
  deleteAppVersion,
  fetchAppFlags,
  createAppFlag,
  updateAppFlag,
  setAppFlagEnabled,
  deleteAppFlag,
  fetchAppPolicy,
  saveAppPolicy,
  isOk
} from '@/api/admin'

/**
 * 状态机允许的目标状态 —— 与后端 AppVersionStatus#allowedTargets 保持一致。
 * ⚠️ 前端这份只用来决定「显示哪些按钮」，真正的校验在后端（非法流转返回 9523）。
 */
const TRANSITIONS = {
  DRAFT: ['PRE', 'PUBLISHED', 'OFFLINE'],
  PRE: ['PUBLISHED', 'OFFLINE'],
  PUBLISHED: ['OFFLINE'],
  OFFLINE: ['PUBLISHED']
}

const ENV_LABELS = { PROD: '生产', PRE: '预发', TEST: '测试' }

export default {
  name: 'AppControlPanel',
  data() {
    return {
      platform: 'ANDROID',
      env: 'PROD',
      tab: 'versions',
      loading: false,
      versions: [],
      flags: [],
      policy: {
        updateCheckEnabled: true,
        kickAllDevices: false,
        kickAllAt: null,
        hotfixEnabled: false,
        blockedVersions: [],
        debugWhitelist: [],
        persisted: false,
        updatedAt: null
      },
      policyForm: { blockedVersionsText: '', debugWhitelistText: '' },
      policySaving: false,

      versionDialogVisible: false,
      versionSaving: false,
      versionForm: this.emptyVersionForm(),
      versionRules: {
        versionName: [{ required: true, message: '请填写版本名', trigger: 'blur' }],
        versionCode: [{ required: true, message: '请填写版本号', trigger: 'blur' }],
        updateType: [{ required: true, message: '请选择更新类型', trigger: 'change' }]
      },

      flagDialogVisible: false,
      flagSaving: false,
      flagForm: this.emptyFlagForm(),
      flagRules: {
        flagKey: [
          { required: true, message: '请填写开关键', trigger: 'blur' },
          {
            pattern: /^[a-zA-Z][a-zA-Z0-9_.]{1,63}$/,
            message: '只能由字母、数字、下划线和点组成，且以字母开头',
            trigger: 'blur'
          }
        ],
        name: [{ required: true, message: '请填写名称', trigger: 'blur' }]
      }
    }
  },
  computed: {
    envLabel() {
      return ENV_LABELS[this.env] || this.env
    }
  },
  created() {
    this.reloadAll()
  },
  methods: {
    // ---------------------------- 数据加载 ----------------------------

    /**
     * 重新加载三个子模块。
     * 平台 / 环境切换后必须全部重拉：只重拉当前 tab 会让另外两个 tab 的数据
     * 停留在旧环境，一旦在这里改了值就是「改测试环境却以为在改生产」。
     */
    reloadAll() {
      this.loadVersions()
      this.loadFlags()
      this.loadPolicy()
    },
    loadVersions() {
      this.loading = true
      fetchAppVersions({ platform: this.platform, env: this.env })
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '版本列表加载失败')
          }
          this.versions = res.data || []
        })
        .catch(err => this.$message.error(err.message || '版本列表加载失败'))
        .finally(() => {
          this.loading = false
        })
    },
    loadFlags() {
      fetchAppFlags({ platform: this.platform, env: this.env })
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '开关列表加载失败')
          }
          this.flags = res.data || []
        })
        .catch(err => this.$message.error(err.message || '开关列表加载失败'))
    },
    loadPolicy() {
      fetchAppPolicy({ platform: this.platform, env: this.env })
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '平台策略加载失败')
          }
          const data = res.data || {}
          this.policy = {
            updateCheckEnabled: data.updateCheckEnabled !== false,
            kickAllDevices: !!data.kickAllDevices,
            kickAllAt: data.kickAllAt || null,
            hotfixEnabled: !!data.hotfixEnabled,
            blockedVersions: data.blockedVersions || [],
            debugWhitelist: data.debugWhitelist || [],
            persisted: !!data.persisted,
            updatedAt: data.updatedAt || null
          }
          this.policyForm.blockedVersionsText = (data.blockedVersions || []).join('\n')
          this.policyForm.debugWhitelistText = (data.debugWhitelist || []).join('\n')
        })
        .catch(err => this.$message.error(err.message || '平台策略加载失败'))
    },

    // ---------------------------- 版本 ----------------------------

    emptyVersionForm() {
      return {
        id: null,
        versionName: '',
        versionCode: null,
        minVersionCode: 0,
        updateType: 'SUGGEST',
        title: '',
        notes: '',
        downloadUrl: '',
        storeUrlsText: '',
        grayPercent: 100,
        whitelistText: '',
        channelScopeText: '',
        startTime: null,
        endTime: null
      }
    },
    openVersionDialog(row) {
      if (row) {
        this.versionForm = {
          id: row.id,
          versionName: row.versionName,
          versionCode: row.versionCode,
          minVersionCode: row.minVersionCode,
          updateType: row.updateType,
          title: row.title,
          notes: row.notes,
          downloadUrl: row.downloadUrl,
          storeUrlsText: (row.storeUrls || []).join('\n'),
          grayPercent: row.grayPercent,
          whitelistText: (row.whitelist || []).join('\n'),
          channelScopeText: (row.channelScope || []).join('\n'),
          startTime: row.startTime,
          endTime: row.endTime
        }
      } else {
        this.versionForm = this.emptyVersionForm()
      }
      this.versionDialogVisible = true
    },
    onVersionDialogClosed() {
      this.versionForm = this.emptyVersionForm()
    },
    onSaveVersion() {
      if (this.versionSaving) return
      const form = this.$refs.versionForm
      if (!form) return
      form.validate(valid => {
        if (!valid) return
        const payload = {
          platform: this.platform,
          env: this.env,
          versionName: this.versionForm.versionName,
          versionCode: Number(this.versionForm.versionCode),
          minVersionCode: Number(this.versionForm.minVersionCode || 0),
          updateType: this.versionForm.updateType,
          title: this.versionForm.title,
          notes: this.versionForm.notes,
          downloadUrl: this.versionForm.downloadUrl,
          storeUrls: this.splitList(this.versionForm.storeUrlsText),
          grayPercent: Number(this.versionForm.grayPercent),
          whitelist: this.splitList(this.versionForm.whitelistText),
          channelScope: this.splitList(this.versionForm.channelScopeText),
          startTime: this.versionForm.startTime || null,
          endTime: this.versionForm.endTime || null
        }
        this.versionSaving = true
        const req = this.versionForm.id
          ? updateAppVersion(this.versionForm.id, payload)
          : createAppVersion(payload)
        req
          .then(res => {
            if (!isOk(res)) {
              throw new Error((res && res.message) || '保存失败')
            }
            this.$message.success(
              this.versionForm.id ? '已保存' : '已保存为草稿，确认无误后请点「发布」'
            )
            this.versionDialogVisible = false
            this.loadVersions()
          })
          .catch(err => this.$message.error(err.message || '保存失败'))
          .finally(() => {
            this.versionSaving = false
          })
      })
    },
    canTransit(row, target) {
      const allowed = TRANSITIONS[row.status] || []
      return allowed.indexOf(target) >= 0
    },
    onChangeVersionStatus(row, target) {
      const label = { PRE: '预发布', PUBLISHED: '发布', OFFLINE: '下线' }[target] || target
      const tips = {
        PRE: '预发布只对白名单可见，用于内部验证。',
        PUBLISHED: '发布后会按灰度 / 白名单 / 时间段下发给客户端。',
        OFFLINE: '下线后不再下发，行会保留作为历史归档。'
      }
      this.$confirm(`${tips[target] || ''}确定要${label}版本「${row.versionName}」吗？`, `${label}版本`, {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: target === 'OFFLINE' ? 'warning' : 'info'
      })
        .then(() => changeAppVersionStatus(row.id, target))
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || `${label}失败`)
          }
          this.$message.success(`${label}成功`)
          this.loadVersions()
        })
        .catch(err => {
          if (err === 'cancel' || err === 'close') return
          this.$message.error((err && err.message) || `${label}失败`)
        })
    },
    onDeleteVersion(row) {
      this.$confirm(
        `删除后无法恢复。确定删除版本「${row.versionName}」（code ${row.versionCode}）吗？`,
        '删除版本',
        { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
      )
        .then(() => deleteAppVersion(row.id))
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '删除失败')
          }
          this.$message.success('已删除')
          this.loadVersions()
        })
        .catch(err => {
          if (err === 'cancel' || err === 'close') return
          this.$message.error((err && err.message) || '删除失败')
        })
    },

    // ---------------------------- 开关 ----------------------------

    emptyFlagForm() {
      return {
        id: null,
        flagKey: '',
        name: '',
        description: '',
        group: 'FEATURE',
        enabled: true,
        defaultValue: false,
        rolloutType: 'ALL',
        rolloutPercent: 100,
        whitelistText: '',
        startTime: null,
        endTime: null
      }
    },
    openFlagDialog(row) {
      if (row) {
        this.flagForm = {
          id: row.id,
          flagKey: row.flagKey,
          name: row.name,
          description: row.description,
          group: row.group || 'FEATURE',
          enabled: !!row.enabled,
          defaultValue: !!row.defaultValue,
          rolloutType: row.rolloutType || 'ALL',
          rolloutPercent: row.rolloutPercent === undefined ? 100 : row.rolloutPercent,
          whitelistText: (row.whitelist || []).join('\n'),
          startTime: row.startTime,
          endTime: row.endTime
        }
      } else {
        this.flagForm = this.emptyFlagForm()
      }
      this.flagDialogVisible = true
    },
    onFlagDialogClosed() {
      this.flagForm = this.emptyFlagForm()
    },
    onSaveFlag() {
      if (this.flagSaving) return
      const form = this.$refs.flagForm
      if (!form) return
      form.validate(valid => {
        if (!valid) return
        const payload = {
          platform: this.platform,
          env: this.env,
          flagKey: this.flagForm.flagKey,
          name: this.flagForm.name,
          description: this.flagForm.description,
          group: this.flagForm.group,
          enabled: this.flagForm.enabled,
          defaultValue: this.flagForm.defaultValue,
          rolloutType: this.flagForm.rolloutType,
          rolloutPercent: Number(this.flagForm.rolloutPercent),
          whitelist: this.splitList(this.flagForm.whitelistText),
          startTime: this.flagForm.startTime || null,
          endTime: this.flagForm.endTime || null
        }
        this.flagSaving = true
        const req = this.flagForm.id ? updateAppFlag(this.flagForm.id, payload) : createAppFlag(payload)
        req
          .then(res => {
            if (!isOk(res)) {
              throw new Error((res && res.message) || '保存失败')
            }
            this.$message.success('已保存')
            this.flagDialogVisible = false
            this.loadFlags()
          })
          .catch(err => this.$message.error(err.message || '保存失败'))
          .finally(() => {
            this.flagSaving = false
          })
      })
    },
    /**
     * 列表里直接切总闸。
     *
     * el-switch 已经先把 row.enabled 改掉了，失败时必须重新拉列表把它改回去 ——
     * 否则页面显示「已关闭」而库里其实没变，比报错更危险。
     */
    onToggleFlag(row) {
      const target = !!row.enabled
      setAppFlagEnabled(row.id, target)
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '切换失败')
          }
          this.$message.success(target ? '已开启' : '已关闭')
        })
        .catch(err => {
          this.$message.error(err.message || '切换失败')
          this.loadFlags()
        })
    },
    onDeleteFlag(row) {
      this.$confirm(
        `删除后客户端读不到该键，会退回自己的本地默认值。确定删除开关「${row.flagKey}」吗？`,
        '删除开关',
        { confirmButtonText: '确定删除', cancelButtonText: '取消', type: 'warning' }
      )
        .then(() => deleteAppFlag(row.id))
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '删除失败')
          }
          this.$message.success('已删除')
          this.loadFlags()
        })
        .catch(err => {
          if (err === 'cancel' || err === 'close') return
          this.$message.error((err && err.message) || '删除失败')
        })
    },

    // ---------------------------- 策略 ----------------------------

    /** 版本 tab 里的「版本检测」开关：只改这一个字段，其余保持原值提交 */
    onToggleUpdateCheck(value) {
      this.savePolicy({ updateCheckEnabled: value })
        .then(() => this.$message.success(value ? '已开启版本检测' : '已关闭版本检测'))
        .catch(() => {
          // 失败回滚：把开关状态改回去，避免页面与库里不一致
          this.policy.updateCheckEnabled = !value
        })
    },
    onSavePolicy() {
      this.savePolicy({})
        .then(() => this.$message.success('策略已保存'))
        .catch(() => {})
    },
    /**
     * 保存策略。
     *
     * @param {object} overrides 需要覆盖的字段（版本 tab 只改版本检测开关时用）
     * @returns {Promise}
     */
    savePolicy(overrides) {
      if (this.policySaving) return Promise.reject(new Error('正在保存'))
      const payload = {
        platform: this.platform,
        env: this.env,
        updateCheckEnabled: this.policy.updateCheckEnabled,
        kickAllDevices: this.policy.kickAllDevices,
        hotfixEnabled: this.policy.hotfixEnabled,
        blockedVersions: this.splitNumbers(this.policyForm.blockedVersionsText),
        debugWhitelist: this.splitList(this.policyForm.debugWhitelistText),
        ...overrides
      }
      this.policySaving = true
      return saveAppPolicy(payload)
        .then(res => {
          if (!isOk(res)) {
            throw new Error((res && res.message) || '保存失败')
          }
          this.loadPolicy()
          return res
        })
        .catch(err => {
          this.$message.error(err.message || '保存失败')
          throw err
        })
        .finally(() => {
          this.policySaving = false
        })
    },

    // ---------------------------- 工具 ----------------------------

    /** 文本域 → 字符串数组（换行 / 逗号 / 中文逗号都算分隔符） */
    splitList(text) {
      if (!text) return []
      return String(text)
        .split(/[\n,，;；]/)
        .map(item => item.trim())
        .filter(item => item.length > 0)
    },
    /** 文本域 → 正整数数组（版本号） */
    splitNumbers(text) {
      return this.splitList(text)
        .map(item => Number(item))
        .filter(item => Number.isInteger(item) && item > 0)
    },
    statusTagType(status) {
      if (status === 'PUBLISHED') return 'success'
      if (status === 'PRE') return 'warning'
      if (status === 'OFFLINE') return 'info'
      return ''
    },
    updateTypeTagType(type) {
      if (type === 'FORCE') return 'danger'
      if (type === 'SILENT') return 'info'
      return 'warning'
    },
    timeRange(row) {
      const start = row.startTime ? this.formatTime(row.startTime) : '立即'
      const end = row.endTime ? this.formatTime(row.endTime) : '长期'
      return `${start} ~ ${end}`
    },
    formatTime(value) {
      if (!value) return '-'
      return String(value).replace('T', ' ').slice(0, 16)
    }
  }
}
</script>

<style scoped>
/* 卡片 / 筛选栏 / 标签等通用样式统一来自 @/styles/admin.css */
.adm-app-tabs {
  margin-top: 16px;
}

.adm-w140 {
  width: 140px;
}

.adm-w180 {
  width: 180px;
}

.adm-app-ver {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.adm-app-ver-name {
  font-weight: 600;
}

.adm-app-time {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.adm-app-form {
  padding: 4px 8px 0 0;
}
</style>
